import { useEffect, useRef, useState } from "react";
import { Modal } from "bootstrap";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { motion, AnimatePresence } from "framer-motion";
import { getAuthUser } from "../../services/auth";

import VendorTable from "./VendorTable";
import VendorModal from "./VendorModal";
import ImportModal from "./ImportModal";
import SearchInput from "./SearchInput";
import PaginationControl from "./PaginationControl";
import useVendorForm from "./useVendorForm";
import ChartVendor from "./ChartVendor";
import { Skeleton } from "primereact/skeleton";

export default function Vendor() {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [editId, setEditId] = useState(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [vendors, setVendors] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [totalDataVendorMO, setTotalDataVendorMO] = useState([]);
  const [showChart, setShowChart] = useState(true);

  const modalRef = useRef(null);
  const importModalRef = useRef(null);
  const fileInputRef = useRef();
  const navigate = useNavigate();
  const auth = getAuthUser();

  const {
    form,
    setForm,
    errors,
    validated,
    setValidated,
    validate,
    handleChange,
    resetForm,
    setErrors,
  } = useVendorForm();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 2000);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    fetchVendors(1, debouncedSearch);
  }, [debouncedSearch]);

  const fetchVendors = async (page = 1, search = "", perPageSize = perPage) => {
    setTableLoading(true);
    try {
      const res = await API.get("/database-vendors", {
        params: {
          page: page,
          per_page: perPageSize,
          search: search,
        },
      });
      setVendors(res.data.data);
      setTotalDataVendorMO(res.data.userVendor);
      setTotalPages(res.data.meta.last_page);
      setTotalData(res.data.meta.total);
      setTableLoading(false);
    } catch (err) {
      setTableLoading(false);
      toast.error("Sesi habis. Silakan login ulang.");
      navigate("/login");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formEl = e.currentTarget;

    const html5Valid = formEl.checkValidity();
    const customValid = validate();

    if (!html5Valid || !customValid) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    setSubmitLoading(true);
    try {
      if (editId) {
        await API.put(`/database-vendors/${editId}`, form);
        toast.info("Vendor berhasil diperbarui.");
      } else {
        await API.post("/database-vendors", form);
        toast.success("Vendor berhasil ditambah.");
      }

      resetForm();
      setEditId(null);
      fetchVendors();

      const modalInstance =
        Modal.getInstance(modalRef.current) || new Modal(modalRef.current);
      modalInstance.hide();

      document.body.classList.remove("modal-open");
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());

      setValidated(false);
      setSubmitLoading(false);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || "Gagal menyimpan vendor!";
      toast.error(errorMessage);
      setSubmitLoading(false);
    }
  };

  const handleEdit = (vendor) => {
    const decodedPhone = vendor.phone ? atob(vendor.phone) : "";
    setForm({
      ...vendor,
      phone: decodedPhone,
    });
    setEditId(vendor.id);
    new Modal(modalRef.current).show();
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Yakin?",
      text: "Vendor akan dihapus secara permanen.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
    });
    if (result.isConfirmed) {
      try {
        await API.delete(`/database-vendors/${id}`);
        toast.success("Vendor berhasil dihapus!");
        fetchVendors();
      } catch (err) {
        toast.error("Gagal menghapus Vendor.");
      }
    }
  };

  const handleExport = async (type) => {
    setExportLoading(true);
    try {
      if (type === "general") {
        const res = await API.get("/vendors/export", {
          responseType: "blob",
          params: {
            search: search,
          },
        });
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "vendors.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        setExportLoading(false);
      } else {
        const res = await API.get("/vendors/exportWoowa", {
          responseType: "blob",
          params: {
            search: search,
          },
        });
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "vendors-Woowa.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        setExportLoading(false);
      }
    } catch (error) {
      setExportLoading(false);
      toast.error("Export gagal: " + error.message);
    }
  };

  const handleImport = async (formData) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      const response = await API.post("/vendors/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (event) => {
          const percent = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(percent);
        },
      });
      const result = response?.data;

      toast.success(
        `✅ Import selesai!\n\n📥 Ditambahkan : ${result.inserted}\n🔄 Diperbarui  : ${result.updated}\n❌ Gagal       : ${result.failed}`,
        {
          style: { whiteSpace: "pre-line" },
          autoClose: false,
          closeOnClick: false,
          closeButton: true,
        }
      );

      fetchVendors();
      Modal.getInstance(importModalRef.current)?.hide();
    } catch (err) {
      toast.error(
        "Gagal import: " + (err.response?.data?.message || err.message)
      );
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div>
      <AnimatePresence>
        {showChart && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ChartVendor chartData={totalDataVendorMO} />
          </motion.div>
        )}
      </AnimatePresence>
      <button
        className="btn btn-primary my-2"
        onClick={() => setShowChart((prev) => !prev)}
      >
        {showChart ? "Hide Chart" : "Show Chart"}{" "}
      </button>

      {tableLoading ? (
        <div className="d-flex justify-content-between gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <Skeleton width="80px" height="20px" />
            <Skeleton width="90px" height="20px" />
            <Skeleton width="300px" height="20px" />
          </div>

          <div className="d-flex align-items-center gap-2">
            <Skeleton width="130px" height="20px" />
            <Skeleton width="110px" height="20px" />
            <Skeleton width="110px" height="20px" />
            <Skeleton width="110px" height="20px" />
          </div>
        </div>
      ) : (
        <div className="d-flex justify-content-between gap-1 mb-3">
          <div className="d-flex align-items-center gap-2">
            <label className="me-2">Tampilkan:</label>
            <select
              className="form-select d-inline-block w-auto"
              value={perPage}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setPerPage(newSize);
                setPage(1);
                fetchVendors(1, search, newSize);
              }}
            >
              {[50, 100, 500, 1000].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="ms-2">
              Page {page} of {totalPages} from{" "}
              {totalData.toLocaleString("id-ID")} Vendor
            </span>
          </div>
          <div className="d-flex align-items-center justify-content-end gap-2 ">
            {!tableLoading && auth?.role === "admin" && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setEditId(null);
                    resetForm();
                    new Modal(modalRef.current).show();
                  }}
                >
                  <i className="fa-solid fa-plus"></i> Tambah Kontak
                </button>

                <button
                  className="btn btn-warning text-white"
                  onClick={() => {
                    new Modal(importModalRef.current).show();
                  }}
                >
                  <i className="fa-solid fa-upload"></i> Import
                </button>
              </>
            )}
            <div className="dropdown">
              <button
                className="btn btn-success dropdown-toggle"
                type="button"
                id="dropdownMenuButton1"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin me-1"></i>{" "}
                    Mengekspor...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-file-download me-1"></i> Export
                  </>
                )}
              </button>

              <ul
                className="dropdown-menu"
                aria-labelledby="dropdownMenuButton1"
              >
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => handleExport("general")}
                    disabled={exportLoading}
                  >
                    <i className="fa-solid fa-file-excel me-1"></i> Excel
                    General
                  </button>
                </li>

                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => handleExport("woowa")}
                    disabled={exportLoading}
                  >
                    <i className="fa-brands fa-whatsapp me-1"></i> Excel Woowa
                  </button>
                </li>
              </ul>
            </div>

            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      )}

      <VendorTable
        vendors={vendors}
        tableLoading={tableLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        page={page}
        perPageSize={perPage}
        search={search}
      />

      <PaginationControl
        page={page}
        totalPages={totalPages}
        totalData={totalData}
        perPage={perPage}
        onPrev={() => {
          const newPage = page - 1;
          setPage(newPage);
          fetchVendors(newPage, search, perPage);
        }}
        onNext={() => {
          const newPage = page + 1;
          setPage(newPage);
          fetchVendors(newPage, search, perPage);
        }}
        onGoToPage={(newPage) => {
          setPage(newPage);
          fetchVendors(newPage, search, perPage);
        }}
        onChangeRows={(newSize) => {
          setPerPage(newSize);
          setPage(1);
          fetchVendors(1, search, newSize); // ✅ fungsi kamu
        }}
      />

      <ImportModal
        modalRef={importModalRef}
        fileInputRef={fileInputRef}
        onImport={handleImport}
        uploadProgress={uploadProgress}
        uploading={uploading}
      />
      <VendorModal
        modalRef={modalRef}
        form={form}
        editId={editId}
        onChange={handleChange}
        onSubmit={handleSubmit}
        submitLoading={submitLoading}
        validated={validated}
        errors={errors}
        onCancelEdit={() => {
          setEditId(null);
          resetForm();
        }}
        setValidated={setValidated}
        setErrors={setErrors}
      />
    </div>
  );
}
